import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import { RoutePaths } from "../../../utility/RoutePaths";
import {
  getPastPapersAPI,
  createPastPaperAPI,
  updatePastPaperAPI,
  deletePastPaperAPI,
} from "../../../services/portal/admin/pastPaperService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import { tableHeaderColor } from "../../../utility/Common";
import RemoveConfirmationDialog from "../../../components/portal/RemoveConfirmationDialog";

const ManagePastPapers = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pastPapers, setPastPapers] = useState([]);
  const [editingDetails, setEditingDetails] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    videoLink: "",
  });

  useEffect(() => {
    loadPastPapers();
  }, []);

  const loadPastPapers = async () => {
    setLoading(true);
    try {
      const res = await getPastPapersAPI();
      if (res && res.IsSuccessful && res.Data) {
        setPastPapers(res.Data);
      }
    } catch (error) {
      console.error("Error loading past papers:", error);
      showErrorToastMsg("Failed to load past papers");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showErrorToastMsg("Please enter a title");
      return;
    }

    if (!formData.description.trim()) {
      showErrorToastMsg("Please enter a description");
      return;
    }

    setSaving(true);
    try {
      const params = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        videoLink: formData.videoLink.trim(),
      };

      let res;
      if (editingDetails) {
        params.Id = editingDetails.Id;
        res = await updatePastPaperAPI(params);
      } else {
        res = await createPastPaperAPI(params);
      }

      if (res && res.IsSuccessful) {
        showSuccessToastMsg(
          res.Message ||
            (editingDetails ? "Updated successfully" : "Saved successfully"),
        );
        setEditingDetails(null);
        clearFields();
        loadPastPapers();
      } else {
        showErrorToastMsg(
          res?.Message || "Something went wrong, please try again",
        );
      }
    } catch (error) {
      console.error("Error saving past paper:", error);
      showErrorToastMsg("Failed to save past paper");
    } finally {
      setSaving(false);
    }
  };

  const handleEditDetails = (item) => {
    setEditingDetails(item);
    setFormData({
      title: item.Title,
      description: item.Description,
      videoLink: item.VideoLink || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingDetails(null);
    clearFields();
  };

  const clearFields = () => {
    setFormData({ title: "", description: "", videoLink: "" });
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const res = await deletePastPaperAPI(deleteItem.Id);
      if (res.IsSuccessful) {
        loadPastPapers();
        showSuccessToastMsg("Deleted successfully");
      } else {
        showErrorToastMsg(res.Message || "Failed to delete");
      }
      setShowDeleteConfirm(false);
      setDeleteItem(null);
    } catch (error) {
      console.error("Error deleting past paper:", error);
      showErrorToastMsg("Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteItem(null);
  };

  return (
    <DashboardLayout
      title="Manage Past Papers"
      subtitle="Add and manage past papers"
    >
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-6">
          Manage Past Papers
        </h3>

        {/* Add/Edit Form */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              {editingDetails ? "Edit Past Paper" : "Add New Past Paper"}
            </h4>
          </div>
          <div className="p-6">
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter title"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter description"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video Link
                </label>
                <input
                  type="text"
                  name="videoLink"
                  value={formData.videoLink}
                  onChange={handleChange}
                  placeholder="Enter video link"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-2 items-end justify-end md:col-span-3">
                {editingDetails && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="text-sm font-medium px-4 py-1.5 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving}
                  className={`text-md font-medium px-4 py-1.5 rounded-md transition-colors ${
                    saving
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-primary-600 text-white hover:bg-primary-700"
                  }`}
                >
                  {saving ? "Saving..." : editingDetails ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Past Papers Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">Past Papers List</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Video Link
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manage Files
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : pastPapers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      No past papers found.
                    </td>
                  </tr>
                ) : (
                  pastPapers.map((item) => (
                    <tr key={item.PastPaperId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.Title}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.Description}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.VideoLink ? (
                          <a
                            href={item.VideoLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-800 underline"
                          >
                            View
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        <button
                          onClick={() =>
                            navigate(
                              `${RoutePaths.ADMIN_MANAGE_PAST_PAPER_FILES_PAGE}?pastPaperId=${item.Id}`,
                            )
                          }
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Files
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-4">
                        <button
                          onClick={() => handleEditDetails(item)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showDeleteConfirm && (
          <RemoveConfirmationDialog
            loading={loading}
            description={deleteItem.Title}
            cancelDelete={cancelDelete}
            confirmDelete={confirmDelete}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ManagePastPapers;
